import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { History, Loader2, ShieldCheck, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_LV = {
  account_update: 'Konta iestatījumi',
  account_pause: 'Konta pauze',
  page_update: 'Lapa rediģēta',
  page_create: 'Lapa izveidota',
  page_delete: 'Lapa dzēsta',
  page_pause: 'Lapa pauzēta',
  page_resume: 'Lapa atsākta',
  page_regenerate: 'Lapa atjaunota no saites',
  page_generate: 'Lapa ģenerēta no saites',
  page_reorder: 'Lapas pārkārtotas',
};
const ACTION_EN = {
  account_update: 'Account settings',
  account_pause: 'Account pause',
  page_update: 'Page edited',
  page_create: 'Page created',
  page_delete: 'Page deleted',
  page_pause: 'Page paused',
  page_resume: 'Page resumed',
  page_regenerate: 'Page refreshed from URL',
  page_generate: 'Page generated from URL',
  page_reorder: 'Pages reordered',
};
const ACTION_COLOR = {
  page_delete: 'bg-red-100 text-red-700',
  page_create: 'bg-green-100 text-green-700',
  page_generate: 'bg-green-100 text-green-700',
  page_regenerate: 'bg-blue-100 text-blue-700',
  page_update: 'bg-amber-100 text-amber-700',
};

export default function AdminAuditTab({ language }) {
  const isLV = language === 'lv';
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const PAGE = 25;

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/admin/audit-log`, { params: { skip: page * PAGE, limit: PAGE } });
      setRows(data.log || []);
      setTotal(data.total || 0);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { load(); }, [load]);

  const fmt = (iso) => {
    if (!iso) return '-';
    try {
      const d = new Date(iso);
      return d.toLocaleString(isLV ? 'lv-LV' : 'en-GB', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  const label = (a) => (isLV ? ACTION_LV : ACTION_EN)[a] || a;
  const pages = Math.ceil(total / PAGE);

  return (
    <div data-testid="admin-audit-tab">
      <div className="flex items-center gap-2 mb-1">
        <History className="w-5 h-5 text-[#D97736]" />
        <h2 className="text-xl font-semibold text-[#121212]">{isLV ? 'Admin darbību žurnāls' : 'Admin activity log'}</h2>
      </div>
      <p className="text-sm text-[#5C5C5C] mb-5">
        {isLV
          ? 'Katra izmaiņa, ko admins veic cita lietotāja uzņēmuma lapās (atbalsta režīmā), ar laiku un lietotāju.'
          : 'Every change an admin makes on another user\u2019s business pages (support mode), with time and user.'}
      </p>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#D97736]" /></div>
      ) : rows.length === 0 ? (
        <div className="text-center py-16 text-[#5C5C5C]" data-testid="admin-audit-empty">
          <ShieldCheck className="w-10 h-10 mx-auto mb-3 text-[#CFCFCF]" />
          {isLV ? 'Vēl nav neviena admin labojuma klientu lapās.' : 'No admin edits on client pages yet.'}
        </div>
      ) : (
        <>
          <div className="overflow-x-auto border border-[#E5E4E0] rounded-xl bg-white">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E5E4E0] bg-[#FAFAFA] text-left text-[#5C5C5C]">
                  <th className="px-4 py-3 font-medium">{isLV ? 'Laiks' : 'Time'}</th>
                  <th className="px-4 py-3 font-medium">{isLV ? 'Admins' : 'Admin'}</th>
                  <th className="px-4 py-3 font-medium">{isLV ? 'Klients' : 'Client'}</th>
                  <th className="px-4 py-3 font-medium">{isLV ? 'Darbība' : 'Action'}</th>
                  <th className="px-4 py-3 font-medium">{isLV ? 'Detaļas' : 'Details'}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-[#F3F2EE] last:border-0 hover:bg-[#FAFAFA]" data-testid={`audit-row-${r.id}`}>
                    <td className="px-4 py-3 text-[#5C5C5C] whitespace-nowrap">{fmt(r.at)}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-[#121212]">{r.admin_name || '-'}</div>
                      <div className="text-xs text-[#9A9A9A]">{r.admin_email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[#121212]">{r.biz_name || r.biz_user_id}</span>
                        {r.biz_user_id && (
                          <a href={`/business-templates?admin_biz=${r.biz_user_id}&admin_name=${encodeURIComponent(r.biz_name || '')}`}
                            target="_blank" rel="noopener noreferrer" title={isLV ? 'Atvērt uzņēmumu' : 'Open business'}
                            className="text-[#9A9A9A] hover:text-[#D97736]" data-testid={`audit-open-biz-${r.id}`}>
                            <ExternalLink className="w-3.5 h-3.5" />
                          </a>
                        )}
                      </div>
                      {r.page_label && <div className="text-xs text-[#9A9A9A]">{r.page_label}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-1 rounded-md text-xs font-medium ${ACTION_COLOR[r.action] || 'bg-[#F0EFEA] text-[#5C5C5C]'}`}>
                        {label(r.action)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-[#5C5C5C] max-w-xs">
                      {r.summary && <div>{r.summary}</div>}
                      {r.changed_fields && r.changed_fields.length > 0 && (
                        <div className="text-xs text-[#9A9A9A] mt-0.5">{r.changed_fields.join(', ')}</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pages > 1 && (
            <div className="flex items-center justify-between mt-4 text-sm text-[#5C5C5C]">
              <span>{isLV ? `${total} ieraksti` : `${total} entries`}</span>
              <div className="flex items-center gap-2">
                <button disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-[#E5E4E0] disabled:opacity-40 hover:border-[#D97736]" data-testid="audit-prev">
                  <ChevronLeft className="w-4 h-4" /> {isLV ? 'Iepriekšējā' : 'Prev'}
                </button>
                <span>{page + 1} / {pages}</span>
                <button disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-[#E5E4E0] disabled:opacity-40 hover:border-[#D97736]" data-testid="audit-next">
                  {isLV ? 'Nākamā' : 'Next'} <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
